import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Brand from "@/components/Brand";
import BadgeRow from "@/components/BadgeRow";
import TitleTag from "@/components/TitleTag";
import { FlagBadge } from "@/lib/flags";
import VoiceRecorder from "@/components/VoiceRecorder";
import GiftModal from "@/components/GiftModal";

function memberDuration(iso) {
  if (!iso) return "—";
  const days = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""}`;
  if (days < 365) return `${Math.floor(days / 30)} months`;
  const y = Math.floor(days / 365); const m = Math.floor((days % 365) / 30);
  return `${y} yr${y !== 1 ? "s" : ""}${m ? ` ${m} mo` : ""}`;
}

export default function PublicProfilePage() {
  const { userId } = useParams();
  const { user: me, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState("");
  const [dmOpen, setDmOpen] = useState(false);
  const [points, setPoints] = useState(null);
  const [following, setFollowing] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("inappropriate");
  const [reportDetails, setReportDetails] = useState("");
  const [showGift, setShowGift] = useState(false);

  useEffect(() => {
    (async () => {
      setErr("");
      try {
        const { data } = await api.get(`/social/users/${userId}`);
        setProfile(data);
        if (data.id) {
          try { const { data: p } = await api.get("/challenges/points", { params: { range: "all" } }); setPoints(p); } catch {}
          try {
            const meId = me?.id;
            if (meId) {
              const { data: f } = await api.get(`/social/friends/${meId}`);
              setFollowing((f.following || []).some(x => x.id === userId));
              const { data: b } = await api.get("/social/blocks");
              setBlocked(b.some(x => x.id === userId));
            }
          } catch {}
        }
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) setErr("User not found — they may have deleted their account.");
        else if (status === 403) setErr(fmtErr(detail) || "You don't have permission to view this profile.");
        else if (status >= 500) setErr("Server hiccup loading this profile. Please retry in a moment.");
        else setErr(fmtErr(detail) || e.message || "Couldn't load this profile.");
      }
    })();
  }, [userId, me?.id]);

  async function toggleFollow() {
    try {
      if (following) { await api.post("/social/unfollow", { user_id: userId }); setFollowing(false); }
      else { await api.post("/social/follow", { user_id: userId }); setFollowing(true); }
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function toggleBlock() {
    try {
      if (blocked) { await api.post("/social/unblock", { user_id: userId }); setBlocked(false); }
      else {
        if (!window.confirm("Block this user? They won't be able to view your profile or DM you.")) return;
        await api.post("/social/block", { user_id: userId }); setBlocked(true); setFollowing(false);
      }
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function submitReport() {
    try {
      await api.post("/social/report", { user_id: userId, reason: reportReason, details: reportDetails });
      setReportOpen(false); setReportDetails("");
      alert("Report submitted. Our admins will review it shortly.");
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }

  async function openThread() {
    setDmOpen(true);
    try { const { data } = await api.get(`/social/messages/${userId}`); setThread(data); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function send() {
    if (!draft.trim()) return;
    try {
      const { data } = await api.post("/social/messages", { to: userId, body: draft });
      setThread(t => [...t, data]); setDraft("");
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }

  if (!profile) return (
    <div className="min-h-screen bg-[#0b0e14] text-slate-200 font-mono">
      <Header onLogout={logout}/>
      <div className="max-w-xl mx-auto px-6 py-10 text-center" data-testid="profile-loading">
        {err ? (
          <>
            <i className="fa-solid fa-circle-exclamation text-5xl text-amber-400 mb-4"/>
            <h1 className="text-xl font-bold text-white">Couldn't load profile</h1>
            <p className="text-sm text-slate-400 mt-2" data-testid="profile-error">{err}</p>
            <div className="flex justify-center gap-2 mt-5">
              <Link to="/app" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold text-white">← Back to Sandbox</Link>
              <button onClick={() => window.location.reload()} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-bold text-white">
                <i className="fa-solid fa-arrows-rotate mr-1"/> Retry
              </button>
            </div>
          </>
        ) : (
          <div className="text-slate-500 py-10"><i className="fa-solid fa-spinner fa-spin mr-2"/> Loading…</div>
        )}
      </div>
    </div>
  );

  if (profile.private) {
    return (
      <div className="min-h-screen bg-[#0b0e14] text-slate-200 font-mono">
        <Header onLogout={logout}/>
        <div className="max-w-xl mx-auto px-6 py-10 text-center" data-testid="private-profile">
          <i className="fa-solid fa-lock text-5xl text-slate-600 mb-4"/>
          <h1 className="text-2xl font-bold text-white">{profile.name}</h1>
          <div className="text-sm text-slate-500 mt-1">This profile is private.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0e14] text-slate-200 font-mono" data-testid="public-profile">
      <Header onLogout={logout}/>
      <div className="max-w-4xl mx-auto px-6 py-8 relative">
        <div className="flex justify-between items-start mb-6">
          <Link to="/app" className="text-blue-300 hover:text-blue-200 text-xs">← Back to Sandbox</Link>
          {!profile.is_self && profile.allow_messages && (
            <div className="flex gap-2">
              <button data-testid="send-gift-btn" onClick={() => setShowGift(true)}
                title="Send a gift to this user"
                className="px-3 py-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-200 rounded-lg text-xs font-bold">
                <img src="/ui/gift.png" alt="" className="w-4 h-4 inline-block -mt-0.5 mr-1"/> Send Gift
              </button>
              <button data-testid="follow-btn" onClick={toggleFollow}
                className={`px-3 py-2 rounded-lg text-xs font-bold ${following ? "bg-slate-700 text-slate-200" : "bg-amber-500 text-slate-900"}`}>
                <i className={`fa-solid ${following ? "fa-user-check" : "fa-user-plus"} mr-1`}/>
                {following ? "Following" : "Follow"}
              </button>
              <button data-testid="dm-open-btn" onClick={openThread}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-xs font-bold text-white">
                <i className="fa-solid fa-paper-plane mr-1"/> Message
              </button>
              <button data-testid="report-btn" onClick={() => setReportOpen(true)}
                className="px-2.5 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs font-bold text-slate-300" title="Report">
                <i className="fa-solid fa-flag"/>
              </button>
              <button data-testid="block-btn" onClick={toggleBlock}
                className={`px-2.5 py-2 rounded-lg text-xs font-bold ${blocked ? "bg-rose-600 text-white" : "bg-slate-800 hover:bg-slate-700 text-slate-300"}`} title={blocked ? "Unblock" : "Block"}>
                <i className={`fa-solid ${blocked ? "fa-ban" : "fa-circle-minus"}`}/>
              </button>
            </div>
          )}
        </div>

        <div className="bg-[#11151d] border border-slate-800 rounded-2xl p-6">
          <div className="flex items-start gap-5">
            {profile.avatar
              ? <img src={profile.avatar} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-slate-700"/>
              : <div className="w-24 h-24 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-4xl font-black text-slate-500">{(profile.name||"?").charAt(0).toUpperCase()}</div>}
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 style={profile.name_color ? { color: profile.name_color } : undefined}
                    className={`text-2xl font-bold ${profile.name_color ? "" : "text-white"}`}>{profile.name}</h1>
                <FlagBadge code={profile.country} size="lg"/>
                <TitleTag userId={profile.id} size="md"/>
                <BadgeRow userId={profile.id} size="md"/>
                <span title={profile.online ? "Online" : "Offline"}
                      className={`w-2 h-2 rounded-full ${profile.online ? "bg-emerald-400 animate-pulse" : "bg-slate-600"}`}/>
                <span className="text-[10px] uppercase font-bold text-slate-400">{profile.online ? "Online" : "Offline"}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 text-[10px] uppercase font-bold">
                <Pill color="amber">{profile.role}</Pill>
                <Pill color="blue">{profile.tier}</Pill>
                <Pill color="slate">Member · {memberDuration(profile.member_since)}</Pill>
                {profile.status === "restricted" && <Pill color="rose">Restricted</Pill>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 mt-6">
            <Stat label="Trades"      value={profile.trades}/>
            <Stat label="Win Rate"    value={`${profile.win_rate}%`}/>
            <Stat label="Total PnL"   value={`$${profile.total_pnl}`} color={profile.total_pnl >= 0 ? "text-emerald-300" : "text-rose-300"}/>
            <Stat label="Pts (all-time)" value={points?.points ?? 0}/>
          </div>
          {profile.bio && (
            <div className="mt-5 pt-4 border-t border-slate-800" data-testid="public-bio">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Bio</div>
              <p className="text-sm text-slate-300 italic whitespace-pre-wrap">{profile.bio}</p>
            </div>
          )}
          {profile.can_admin_edit && (
            <div className="mt-4 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="admin-edit-note">
              <i className="fa-solid fa-shield-halved mr-1"/> Admin: manage this account from <Link to="/admin" className="underline">Admin Console</Link>.
            </div>
          )}
        </div>

        {reportOpen && (
          <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4" data-testid="report-modal">
            <div className="bg-[#0f131b] border border-slate-800 rounded-2xl max-w-md w-full p-5">
              <div className="flex justify-between items-start mb-3 border-b border-slate-800 pb-2">
                <h3 className="text-sm font-bold text-white"><i className="fa-solid fa-flag text-rose-400 mr-1"/> Report {profile.name}</h3>
                <button onClick={() => setReportOpen(false)} className="text-slate-500"><i className="fa-solid fa-xmark"/></button>
              </div>
              <select data-testid="report-reason" value={reportReason} onChange={e => setReportReason(e.target.value)}
                className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs mb-2">
                <option value="inappropriate">Inappropriate content</option>
                <option value="harassment">Harassment / threats</option>
                <option value="spam">Spam</option>
                <option value="impersonation">Impersonation</option>
                <option value="other">Other</option>
              </select>
              <textarea data-testid="report-details" rows={4} placeholder="Optional context for the admin queue…" value={reportDetails}
                onChange={e => setReportDetails(e.target.value)}
                className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs resize-none mb-2"/>
              <button data-testid="report-submit" onClick={submitReport}
                className="w-full py-2 bg-rose-600 hover:bg-rose-700 text-white rounded text-xs font-bold">Submit Report</button>
            </div>
          </div>
        )}

        {dmOpen && (          <div className="fixed bottom-4 right-4 w-80 bg-[#0f131b] border border-slate-700 rounded-xl shadow-2xl flex flex-col z-40" data-testid="dm-box">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
              <div className="text-xs font-bold text-white">DM: {profile.name}</div>
              <button onClick={() => setDmOpen(false)} className="text-slate-500 hover:text-rose-300"><i className="fa-solid fa-xmark"/></button>
            </div>
            <div className="flex-1 max-h-64 overflow-y-auto p-3 space-y-2 text-xs">
              {thread.length === 0 && <div className="text-slate-600 text-center py-4">Say hi.</div>}
              {thread.map(m => (
                <div key={m.id} className={`max-w-[80%] px-2.5 py-1.5 rounded-lg ${m.from_id === me?.id ? "ml-auto bg-blue-600/30 text-blue-100" : "bg-slate-800 text-slate-200"}`}>
                  {m.kind === "voice" && m.audio_data_url
                    ? <audio data-testid={`dm-voice-${m.id}`} controls src={m.audio_data_url} className="w-full h-8"/>
                    : <span>{m.body}</span>}
                </div>
              ))}
            </div>
            <div className="p-2 border-t border-slate-800 flex gap-2">
              <input data-testid="dm-input" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
                placeholder="Message…" className="flex-1 px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
              <VoiceRecorder toUserId={userId} onSent={(m) => setThread(t => [...t, m])} disabled={false}/>
              <button data-testid="dm-send" onClick={send} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded text-xs font-bold text-white"><i className="fa-solid fa-paper-plane"/></button>
            </div>
          </div>
        )}
      </div>
      {showGift && <GiftModal initialRecipient={{ id: profile.id, name: profile.name, avatar: profile.avatar }} onClose={() => setShowGift(false)}/>}
    </div>
  );
}

function Header({ onLogout }) {
  return (
    <header className="bg-[#11151d] border-b border-slate-800 px-6 py-3 flex justify-between items-center">
      <Link to="/"><Brand compact/></Link>
      <button onClick={onLogout} className="text-slate-500 hover:text-rose-400 text-xs"><i className="fa-solid fa-right-from-bracket"/></button>
    </header>
  );
}

function Pill({ color, children }) {
  const map = { amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
                blue: "bg-blue-500/10 text-blue-300 border-blue-500/30",
                slate: "bg-slate-500/10 text-slate-300 border-slate-500/30",
                rose: "bg-rose-500/10 text-rose-300 border-rose-500/30" };
  return <span className={`px-2 py-0.5 rounded border ${map[color]}`}>{children}</span>;
}
function Stat({ label, value, color = "text-white" }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
      <div className="text-[9px] uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
